import { PropsWithChildren, useMemo, useState } from 'react';
import { ProjectNavigationCallbacks } from 'shared/interfaces/InitProjectsPageOptions.ts';
import {
    ProjectNavigation,
    ProjectNavigationContext,
} from 'src/projects/navigation/ProjectNavigationContext.ts';

type Props = ProjectNavigationCallbacks & {
    /** Pagination page to render first (1-based). Passed in at app init. */
    initialPage?: number;
};

/**
 * Implements ProjectNavigation without a router. The initial pagination page comes from
 * the init argument, the page is then tracked in local state so pagination works on its own,
 * and every navigation intent is forwarded to the host through optional callbacks so the
 * embedding page can drive real navigation (close the widget, open the home screen, etc.).
 */
export const StandaloneProjectNavigationProvider = ({
    initialPage = 1,
    onPageChange,
    onGoBack,
    onGoHome,
    onNavigate,
    children,
}: PropsWithChildren<Props>) => {
    const [currentPage, setCurrentPage] = useState(() => (initialPage < 1 ? 1 : initialPage));

    const value = useMemo<ProjectNavigation>(() => ({
        currentPage,
        goToPage: (newPage: number) => {
            const next = newPage < 1 ? 1 : newPage;
            setCurrentPage(next);
            onPageChange?.(next);
        },
        goBack: () => onGoBack?.(),
        goHome: () => onGoHome?.(),
        onNavigate: (url: string) => onNavigate?.(url),
    }), [currentPage, onPageChange, onGoBack, onGoHome, onNavigate]);

    return (
        <ProjectNavigationContext.Provider value={value}>
            {children}
        </ProjectNavigationContext.Provider>
    );
};
